// ANGEL FLIGHT — color cockpit render tunables (single source of truth).
//
// The COLOR door-gunner look: a textured world that banks/scrolls below, a
// visible foreground gun viewmodel that sways + recoils, and a unified color
// grade (bloom + vignette + grain + tone-map) that glues the stylized CC0 art
// into one cohesive "lens". Thermal (config/thermal.ts) is now an optional mode
// toggled at runtime; COLOR is the default. No magic numbers in the render layer.

import { BALANCE } from './balance';

export const RENDER = Object.freeze({
  /** Default visual mode. Thermal is a runtime toggle. */
  defaultMode: 'color' as 'color' | 'thermal',

  /** The gunship sensor/eye camera framing (looks down + forward at the band). */
  camera: {
    altitudeM: 14,
    setbackM: 9,
    lookAtZ: 14,
    /** Aim-point height (m). Raising it off the ground tips the pitch UP so the
     *  horizon + burning skyline + a sliver of dusk sky sit in the top of the
     *  frame (G2 reframe — the audit found the sky was NEVER in frame). The
     *  ground still owns ~80% of the picture; the aim raycast adapts. */
    lookAtYM: 7,
    fovDeg: 42, // a touch wider than thermal for a more open, color-world feel
    nearM: 0.5,
    farM: 160, // nudged out (was 120) so the distant skyline silhouette has room to sit behind the fog wall
  },

  /** Fake-flight: the world MOVES under a near-static eye; the rig banks/circles
   *  so it reads as "I'm in a flying aircraft". */
  flight: {
    /** Forward ground scroll speed (metres/sec) — the world sliding beneath
     *  you. NOT a free knob: it MIRRORS the sim's worldDriftMps exactly. The
     *  sim adds the same drift to every ground unit's closing rate, so motion
     *  relative to the terrain is always the unit's own leg speed (≥ 0) —
     *  walkers RUN forward over the asphalt, parked RPG shooters stand their
     *  ground while the bird overtakes them, and nobody ever slides backward
     *  (the twice-audited "they stand still / go backwards" glitch). */
    scrollMps: BALANCE.enemies.worldDriftMps,
    /** Peak bank roll (radians) as the aircraft circles. */
    bankAmpRad: 0.10,
    /** Bank/circle period (seconds). */
    bankPeriodS: 11,
    /** Slight constant nose-down pitch (radians). */
    pitchRad: 0.04,
    /** Lateral sway amplitude (metres) so you must re-acquire targets. Kept
     *  modest — too much sway throws targets out of frame and reads as "they
     *  rush past sideways" instead of "they advance on me". */
    swayAmpM: 1.5,
    swayPeriodS: 7,
    /** Eased follow rate for the bank (exponential damp lambda). */
    bankLambda: 2.2,
    /** SCAN LEGS: a slow cubed-sine cycle that swings the camera's look-at
     *  laterally, so the bird periodically scans off to the SIDE of the lane
     *  instead of staring dead ahead forever. Cubing the sine makes it dwell
     *  ahead and then commit to a side — reads as the pilot banking the orbit,
     *  not a metronome. The aim raycast uses the live camera, so it adapts. */
    scanAmpM: 4,
    scanPeriodS: 23,
  },

  /** The textured ground + props below. */
  world: {
    /** Ground plane size (metres square) — big enough to fill the frustum. */
    groundSizeM: 240,
    /** How many times the asphalt texture tiles across the plane. */
    groundTextureRepeat: 28,
    /** Prop pool: wrecked cars / rubble / barrels that slide past + recycle. */
    propCount: 28,
    /** Spawn depth (world z) where props appear, then slide toward the near edge. */
    propSpawnZ: 70,
    propDespawnZ: -6,
  },

  /** Scenery prop PBR materials — TWO shared materials built once (like the
   *  ground) and applied to the recycled prop pool so the wrecks/barrels/barriers
   *  read as real war-torn surfaces, not flat boxes:
   *   - `metal`    → rusted/scratched (ambientCG Metal032, reuses gunmetal_*)
   *                  for wrecked cars + barrel clusters,
   *   - `concrete` → weathered (ambientCG Concrete016) for barriers + rubble.
   *  Both grade-match the world: SRGB on the colour map, RepeatWrapping, a modest
   *  per-prop tile repeat, anisotropy 4, envMapIntensity = light.envIntensity.
   *  Per-prop tint jitter + scales (in ColorWorld) keep clones from looking
   *  identical. */
  props: {
    metal: {
      /** Warm RUST multiply over the gray scratched-metal map (NOT clean steel). */
      tint: 0x9c6a4a,
      roughness: 0.92, // pitted + oxidised — almost no clean specular
      metalness: 0.7, // still reads as metal; the metalness map modulates it
      normalScale: 1.2, // deepen dents/scratches for the raking sun
      /** Times the metal map tiles across a ~2m prop face. */
      textureRepeat: 1.6,
    },
    concrete: {
      /** Dusty warm-gray multiply — weathered + stained, not a fresh pour. */
      tint: 0xb8ab98,
      roughness: 0.97, // bone-dry rough concrete, no sheen
      metalness: 0.02,
      normalScale: 1.4, // pock-marks + cracks catch the low sun
      textureRepeat: 1.1,
    },
    /** Per-prop tint jitter (±, multiplicative) so no two clones read identical —
     *  small enough to stay "weathered", not rainbow. */
    tintJitter: 0.14,
  },

  /** Asphalt ground material grade — warm tint over the cold gray texture so the
   *  sun rake reads amber, with a faint dusk sheen + punchier surface relief. */
  ground: {
    tint: 0xf0dcbe, // bright warm tan multiply — lifts the dark asphalt map so the sun reads
    roughness: 0.86, // < 1 so the low sun catches a faint specular sheen
    metalness: 0.04,
    normalScale: 1.5, // deepen the asphalt cracks/relief for grazing-light contrast
  },

  /** Lighting (color mode uses real lights + HDRI, not emissive heat). A strong
   *  warm key DOMINATES; a low, cool sky fill only lifts the shadows so they read
   *  blue-against-amber (golden-hour contrast) instead of flat gray. The HDRI is
   *  dialled WAY down — at full strength it washes the whole frame overcast-gray;
   *  here it's just a whisper of reflection on the props, lights carry the mood. */
  light: {
    sunIntensity: 7.5, // strong key — it must visibly OUT-light the ambient to rake the ground
    sunColor: 0xffc878, // warm low sun (amber-gold, not deep orange — keeps lit ground bright)
    ambientIntensity: 0.45, // low fill so the sun owns the lit areas + shadows stay deep
    ambientColor: 0x5870a0, // cool blue sky fill (hemisphere top) — blue shadows vs warm key
    groundFill: 0x4a3826, // warm dusk bounce (hemisphere bottom)
    envIntensity: 0.18, // HDRI barely present — it was the main gray-wash culprit
  },

  /** Distance fog so the world recedes + hides the spawn/recycle line. Warm amber
   *  dusk haze, pushed FAR back so the near + mid ground reads with full contrast
   *  and only the horizon dissolves into atmosphere. */
  fog: {
    color: 0xd99a5e, // warm glowing haze — the distance lifts toward the amber horizon
    near: 64,
    far: 150,
  },

  /** The SPAWN HAZE: a soft fog bank hanging across the far spawn band (sim z
   *  ≈ farZ 26 + pad) so fresh enemies WALK OUT of the murk instead of popping
   *  into the open midfield — the scene's distance fog starts at 64m, far
   *  behind the 26-34m spawn line, so it never covered them. Two parts:
   *   - translucent gradient CURTAIN quads straddling the band (ColorWorld),
   *     tinted to the active theme's fogColor,
   *   - a per-enemy REVEAL fade (GlbEnemyView): rigs are invisible at/beyond
   *     fadeHiddenZ and fully clear once they close past fadeClearZ. */
  spawnHaze: {
    /** Curtain quad sim-depths (m) — a near skirt, front edge, mid band, deep
     *  back wall. The extra near skirt (22m) + lifted opacities make the bank
     *  read as a REAL fog wall the horde has to walk out of (the playtest read
     *  the old faint curtains as "they spawn in the middle of the field"). */
    curtainZ: [22, 25.5, 29, 33.5] as readonly number[],
    curtainOpacity: [0.3, 0.45, 0.58, 0.7] as readonly number[],
    widthM: 90,
    heightM: 11,
    /** Enemy reveal band: opacity 0 at/beyond hiddenZ → 1 at/inside clearZ.
     *  hiddenZ sits ON the far plane (= the shallowest possible spawn,
     *  BALANCE.proj.farZ — coupled, not copied) so EVERY spawn starts fully
     *  invisible and WALKS into the frame through the murk — never a
     *  translucent pop. fadeClearZ is only the FALLBACK clear plane — the live
     *  value comes from the difficulty's revealZM (ColorWorld wires it per
     *  mission), so recruit clears the murk deep at 25m while ace holds the
     *  ghost until 16m. The default (pilot, 21m) lands units FULLY SOLID while
     *  still inside the fog bank — emergence happens AT the fog, never in the
     *  open midfield. */
    fadeHiddenZ: BALANCE.proj.farZ,
    fadeClearZ: 21,
    /** Fog-burst puff at the emergence line: a pooled, fog-tinted sprite that
     *  swells + fades where a unit walks clear of the bank — the "comes out of
     *  the fog" effect that sells the spawn as an arrival, not a pop-in. */
    burst: {
      /** Sprite footprint (world m) at spawn; it swells to ~1.8× over life. */
      sizeM: 2.8,
      lifeMs: 850,
      /** Peak sprite opacity (fades in fast, out slow). */
      opacity: 0.5,
      /** Concurrent puffs (pooled; oldest is re-used round-robin). */
      poolSize: 10,
      /** How many metres BEFORE the clear plane the puff fires — it blooms
       *  while the unit is still half-ghosted, so the puff masks the tail of
       *  the fade. */
      triggerAheadM: 1.5,
    },
  },

  /** Enemy rig gait: the walk/sprint clip playback rate is scaled to the
   *  unit's REAL ground-relative speed (closing rate minus the world drift),
   *  so legs always match motion — fast movers visibly RUN, parked shooters
   *  idle to a shuffle instead of moonwalking in place. The glide bug ("they
   *  slide without walking") came from playing every clip at 1×. */
  gait: {
    /** Ground speed (m/s) at which the authored walk clip plays at 1×. Was
     *  0.85 — far below the sim's 2-6.8 m/s leg speeds, so EVERY unit pinned
     *  the rateMax clamp and pumped its legs frantically (the audited "they
     *  look like they're trying to speed" read). 1.5 + a real sprint clip for
     *  fast movers keeps strides believable. */
    walkRefMps: 1.5,
    /** Ground speed (m/s) at which the authored sprint clip plays at 1×.
     *  3.6 (was 4.2): retuned with the runner's pacing pass — its legs now do
     *  3.6 m/s, so the sprint clip plays at ~1× instead of floaty slow-mo. */
    sprintRefMps: 3.6,
    /** Leg speed (m/s) at/above which a unit uses the SPRINT clip instead of
     *  walk (was keyed off the skin, which put 2.4 m/s walkers in a floaty
     *  slow-mo sprint). 3.4 keeps the slowed runner (3.6 m/s) sprinting. */
    sprintAtMps: 3.4,
    /** Playback-rate clamps (min keeps a visible shuffle, never a freeze). */
    rateMin: 0.55,
    rateMax: 2.2,
    /** Procedural locomotion SWAY layered over the clip. The gunship camera
     *  looks DOWN at the rigs, which foreshortens the authored leg swing to
     *  almost nothing — from overhead a walking soldier reads as a statue
     *  gliding (the audited "they're standing still" read, which clip-rate
     *  scaling alone could not fix). The shoulder ROCK + stride BOB + yaw
     *  swagger below stay clearly visible from above; all advance on the same
     *  gait phase as the clip so legs and sway never desync. */
    sway: {
      /** Stride cycles per second at clip rate 1× (scales with gait rate). */
      strideHz: 1.7,
      /** Side-to-side shoulder rock (radians) — THE overhead walking read. */
      rollRad: 0.1,
      /** Slight alternating torso twist (radians) — the swagger. */
      yawRad: 0.07,
      /** Per-step vertical bounce (model-local metres, pre rig scale). */
      bobM: 0.04,
    },
  },

  /** A vertical dusk-sky gradient drawn as scene.background — far richer than a
   *  flat fog-coloured fill. Deep indigo zenith → warm amber horizon glow. */
  sky: {
    top: 0x1b2b4d, // dusk blue (zenith) — cool counterpoint, darker for tonal range
    mid: 0xb07a54, // warm dusty rose band
    bottom: 0xf6ad5c, // BRIGHT warm amber horizon glow (the sun band) — anchors golden hour
  },

  /** Per-mission BIOME themes. Each overrides just the mood-defining values
   *  (sun/ambient/fog/sky/ground tint) on top of the defaults above, so a mission
   *  reads distinctly by its `theme` key. ColorWorld applies the active one. */
  themes: {
    'dusk-suburb': {
      // The default golden-hour look (matches the base light/fog/sky above).
      sunColor: 0xffc878,
      sunIntensity: 7.5,
      ambientColor: 0x5870a0,
      ambientIntensity: 0.45,
      groundFill: 0x4a3826,
      fogColor: 0xd99a5e,
      fogNear: 64,
      fogFar: 150,
      skyTop: 0x1b2b4d,
      skyMid: 0xb07a54,
      skyBottom: 0xf6ad5c,
      groundTint: 0xf0dcbe,
      skylineFireGlow: 0.55,
      overpassRails: true, // M1 IS "The Overpass" — jersey barriers sell the elevation
    },
    'night-city': {
      // Cold blue night raid: a dim moon key, deep blue shadows, tight haze, and
      // a dark sky with a faint city-glow band on the horizon. Tracers + muzzle +
      // fires POP against the dark.
      sunColor: 0x9fb4e0, // pale cold moonlight
      sunIntensity: 2.6, // dim — the night is dark
      ambientColor: 0x2a3a60,
      ambientIntensity: 0.35,
      groundFill: 0x10141f,
      fogColor: 0x10131e, // near-black blue haze
      fogNear: 40,
      fogFar: 115,
      skyTop: 0x05070f, // near-black zenith
      skyMid: 0x141d33,
      skyBottom: 0x3a3550, // faint distant city glow on the horizon
      groundTint: 0x7d8aa6, // cool wet-asphalt night
      skylineFireGlow: 0.9, // the burning city pops brighter at night
      overpassRails: false, // M2 is a street-level night raid
    },
    'dead-forest': {
      // Sickly green overcast — a fog-choked dead forest. Muted, eerie.
      sunColor: 0xcfe0b0,
      sunIntensity: 4.0,
      ambientColor: 0x4a5a46,
      ambientIntensity: 0.5,
      groundFill: 0x2a2e22,
      fogColor: 0x6b7858, // murky green haze
      fogNear: 36,
      fogFar: 120,
      skyTop: 0x223026,
      skyMid: 0x4a5a44,
      skyBottom: 0x8a9a6e,
      groundTint: 0x9aa886,
      skylineFireGlow: 0.4,
      overpassRails: false,
    },
    'frozen-checkpoint': {
      // Cold WHITEOUT at first light — a snowbound mountain checkpoint (M4). The
      // most distinct read in the roster: BRIGHT + cold, against the warm dusk,
      // the dark night, and the green forest. Pale blue-white storm light, a
      // snow-tinted ground, and a tight near-white haze so the horde looms out of
      // the blizzard close in (the storm itself is half the threat).
      sunColor: 0xdfe9f5, // pale cold storm light
      sunIntensity: 5.0, // flat overcast key, but snow reads bright
      ambientColor: 0x9fb0c8, // cold blue-grey
      ambientIntensity: 0.7, // snow throws a lot of bounce fill
      groundFill: 0x6a7d96, // blue snow-shadow
      fogColor: 0xd8e2ee, // white-blue whiteout
      fogNear: 34, // tight — the storm swallows the field
      fogFar: 100,
      skyTop: 0x9fb2c8, // pale grey-blue zenith
      skyMid: 0xc2cedd,
      skyBottom: 0xe6edf4, // bright storm-lit horizon
      groundTint: 0xeaf0f7, // bright cold snow
      skylineFireGlow: 0.25, // the whiteout swallows distant fires
      overpassRails: false,
    },
  },

  /** Overpass dressing (themes with `overpassRails`): two rows of segmented
   *  concrete jersey barriers scrolling past at the road edges — the cheapest
   *  honest read of "you are flying along an elevated overpass" (M1's name).
   *  One InstancedMesh, fixed instance count, recycled by modular Z offset. */
  overpass: {
    /** |x| (m) of each barrier row — outside the ±9m enemy lanes. */
    railXM: 13,
    railHeightM: 0.85,
    railWidthM: 0.42,
    segmentLenM: 6.5,
    /** Gap between segments (m) — the break makes the scroll motion readable. */
    segmentGapM: 0.8,
    /** Z span covered (sim metres, near → far). */
    spanNearZ: -16,
    spanFarZ: 150,
  },

  /** A distant burning-city SKYLINE silhouette on the horizon — a procedural
   *  CanvasTexture (dark building band + a few spires + soft orange fire-glows)
   *  on wide quads sitting just inside camera.farM, BEHIND fog.far so it reads as
   *  a hazy, low-contrast city on fire at dusk. Counter-scrolls very slowly for
   *  parallax depth so the empty horizon stops reading as a flat fog wall. */
  skyline: {
    /** How far back (world depth, metres) the skyline band sits. Inside the fog
     *  band (near 64 / far 150) so it tints HEAVILY into the dusk haze (soft,
     *  low-contrast) but still reads as a city, and < camera.farM so it isn't
     *  clipped. Closer than fog.far on purpose — exactly at the wall it vanishes.
     *  ~110m keeps the building band readably dark against the amber haze (at the
     *  fog wall it washes out fully) while still sitting clearly "far away". */
    distanceM: 110,
    /** Total lateral span (metres) — wide enough to fill the frame as the camera
     *  banks/sways without revealing the edges. */
    widthM: 480,
    /** Skyline band height (metres) above the horizon. */
    heightM: 70,
    /** Counter-scroll as a fraction of flight.scrollMps (slow = far parallax). */
    parallaxFactor: 0.13,
    /** Fire-glow strength behind the buildings (0..1 alpha of the orange blooms). */
    fireGlow: 0.55,
  },

  /** Drifting SMOKE COLUMNS rising from the burning battlefield: stacks of
   *  additive billboard sprites (dark grey-brown crown, warm ember base) topped
   *  with a small orange fire sprite + a faint flickering PointLight at the base.
   *  Columns slide toward the near edge with the world scroll and recycle to the
   *  far edge — so the city is visibly burning. Counts kept modest for mobile
   *  fill-rate; opacity kept low. */
  smoke: {
    /** Number of independent smoke columns spread across the mid/far field. */
    columnCount: 7,
    /** Billboard sprites stacked per column (rise + grow + fade up the column). */
    spritesPerColumn: 7,
    /** Vertical rise speed of the smoke (metres/sec) — the per-sprite climb cycle. */
    riseSpeedMps: 3.2,
    /** Peak intensity of the flickering fire PointLight at each column base. */
    baseFireLight: 3.0,
    /** Lateral/forward drift bias (metres/sec) added on top of the world scroll. */
    driftMps: 0.4,
    /** Sim-depth (m) at which a column recycles to the far edge. MUST stay well
     *  in FRONT of the near frustum (camera sits at world z ≈ −setbackM): the
     *  old prop-line recycle (z ≈ −10) slid 30m plumes THROUGH the camera plane
     *  as full-frame black slabs (20-40% frame blackouts in the audit). */
    recycleZ: 18,
    /** Fade band (m): column opacity ramps to zero over the last N metres
     *  before recycleZ so the recycle never pops in-frame. */
    nearFadeM: 12,
    /** Minimum |lateral x| (m) a column spawns/recycles at — keeps the plumes
     *  framing the action, never blocking the firing lane or the near frame. */
    minLateralM: 25,
  },

  /** The foreground gun + cockpit viewmodel (rendered as an overlay pass). */
  gun: {
    /** Viewmodel camera FOV (narrower so the gun never clips the world). */
    fovDeg: 50,
    /** Resting position of the gun in the viewmodel camera's local space. */
    restX: 0.0,
    restY: -1.15,
    restZ: -1.6,
    /** Scale applied to the loaded gun GLB (Kenney blasters are ~1 unit). */
    modelScale: 2.2,
    /** How far the gun swings toward the aim point (0..1 of aim offset). */
    aimFollow: 0.55,
    /** Eased follow + sway damp lambda. */
    swayLambda: 9,
    /** Idle bob (engine vibration) amplitude + rate. */
    idleBobAmp: 0.012,
    idleBobHz: 1.6,
    /** Recoil spring constants (per shot kick toward camera + muzzle climb). */
    recoilKick: 0.22, // metres back along z
    recoilStiffness: 190,
    recoilDamping: 19,
    recoilPitch: 0.16, // radians muzzle climb per unit recoil
    /** Barrel spin-up: peak angular velocity (rad/s) at full spinup. */
    barrelSpinMax: 42,

    /** Scratched-metal PBR (ambientCG Metal032) applied to receiver + barrels.
     *  A neutral steel tint multiplies the color map (kept slightly dark so the
     *  gun reads as oiled gunmetal, not bright chrome). UV repeat tiles the worn
     *  detail across the small primitive parts so it never looks stretched. */
    metalTint: 0x9aa0a8, // cool steel multiply over the scratched-metal color map
    metalness: 0.92, // mostly metallic — the metalness map modulates this
    roughness: 0.5, // mid-rough machined metal; the roughness map adds scratches
    metalRepeat: 2.0, // texture tiles per part (receiver/barrel) — worn detail
    normalScale: 1.0, // surface-relief strength from the normal map
    /** Faint procedural specular env (a tiny gradient cube) so the metal catches
     *  moving highlights as it sways — strength kept low so the lights still lead. */
    envIntensity: 0.6,

    /** BARREL HEAT GLOW. A heat accumulator (0..1) rises while the gun is firing
     *  (spinup above the threshold) and decays when it stops; it drives the barrel
     *  material's emissive (orange-red) so the cluster glows hot when the player
     *  hoses and cools when they let off. */
    barrelHeatColor: 0xff5a1e, // hot orange-red emissive at full heat
    barrelHeatMax: 2.4, // peak emissiveIntensity at heat = 1
    /** Spinup above which heat accumulates (the gun is actually firing, not just
     *  spinning down). */
    barrelHeatFireThreshold: 0.55,
    barrelHeatRise: 0.55, // heat units gained per second while firing (~1.8s to glow)
    barrelHeatDecay: 0.4, // heat units lost per second while cooling (~2.5s to cool)
    /** BLOOM-AWARE cap on the barrel emissive (G2): the heat ramp tops out just
     *  above the grade's bloom threshold (0.65) so a hot cluster GLOWS without
     *  blooming into a white blob — the gun must stay a gun at full fire. */
    barrelHeatBloomCap: 1.1,

    /** Mechanical detail dressing (W3) layered on the primitive minigun so it
     *  reads as a real belt-fed rotary cannon, not a box + bare cylinders: a
     *  static barrel SHROUD the cluster emerges from, a top FRONT-SIGHT post,
     *  and a side AMMO CAN. All sit inside the gun's silhouette (well within the
     *  doorframe) so they enrich the gun without eating the frame. Gun-local m. */
    detail: {
      shroudRadius: 0.185, // > the barrel ring (0.155) so it encircles, never clips
      shroudLen: 0.4,
      shroudZ: -0.42,
      sightW: 0.03,
      sightH: 0.14,
      sightZ: -1.02, // near the muzzle end
      sightY: 0.2,
      ammoW: 0.2,
      ammoH: 0.26,
      ammoD: 0.36,
      ammoX: 0.32, // right of the receiver, well inside the doorframe pillars (±1.62)
      ammoY: -0.06,
      ammoZ: 0.16,
    },

    /** REAL cockpit doorframe (G2 reframe). The old look was two floating black
     *  corner slabs eating 20-35% of the frame; this is a slim door OPENING —
     *  thin pillars hugging the side edges, a shallow header bar up top, and a
     *  thicker sill (the cabin floor edge) along the bottom. Composition: the
     *  world framed BY a doorway, not blocked by slabs. Gun-cam local metres. */
    doorframe: {
      color: 0x0b0d11, // near-black airframe interior
      pillarX: 1.62, // |x| of each side pillar (hugs the frame edge at fov 50)
      pillarW: 0.18,
      pillarH: 3.8,
      depthZ: -1.35, // how close the frame sits to the camera
      frameDepth: 0.3, // box depth so the edge catches a hint of light
      headerY: 1.32, // top header bar centre height
      headerH: 0.22,
      sillY: -1.58, // bottom sill (cabin floor edge) centre height
      sillH: 0.42,
      width: 3.7, // header/sill span (pillar-to-pillar)
    },
  },

  /** Color-mode VFX tints (warm, not white-hot). */
  vfx: {
    tracerColor: 0xffd27a,
    muzzleColor: 0xfff0c0,
    impactColor: 0xffe09a,
    explosionColor: 0xff8a3c,
    dustColor: 0x9a8a76,
    /** Muzzle flash point-light pulse intensity + distance. */
    muzzleLightIntensity: 7,
    muzzleLightDistance: 16,
    /** FIXED pool of flash PointLights living in the scene from frame one
     *  (reused round-robin, intensity 0 when idle). Adding/removing lights
     *  per shot changes the scene light count and forces Three to recompile
     *  every standard material — the audited 710ms first-fire hitch. */
    lightPoolSize: 4,
    /** Radius (world m) of the thick tracer tube — fat glowing round, not a line. */
    tracerWidth: 0.06,
    /** DISCRETE tracer rounds (G2): each shot is a short glowing slug that
     *  TRAVELS muzzle→impact (not a full-length instant beam — sustained fire
     *  read as a continuous laser before). Round length is a fraction of the
     *  flight path, clamped; flight time = distance / roundSpeedMps. */
    roundSpeedMps: 110,
    roundLenFrac: 0.22,
    roundLenMinM: 0.6,
    roundLenMaxM: 2.4,
    /** Honest pre-allocated VFX pools (G2): sprites + tracer tubes are built
     *  ONCE at init and reused round-robin — no per-shot geometry/material
     *  allocation at 17 rounds/sec (tube pool size = BALANCE.pools.tracers). */
    spritePoolSize: 64,
    /** Permanent ground decals (G2): pooled InstancedMesh quads stamped where
     *  rounds bite the ground / kills land (budget = BALANCE.pools.blooddecals;
     *  oldest stamp is overwritten round-robin). */
    decalSizeM: 0.55,
    decalColor: 0x14100c,
    decalOpacity: 0.55,

    /** Muzzle-flash sizing (W3): the white-hot core + the warm flash around it.
     *  Bumped a touch over the old inline values so the bang reads punchier. */
    muzzleCoreScale: 0.17,
    muzzleFlashScale: 0.27,

    /** KILL SHOCKWAVE (W3): a flat ground ring that snaps out + fades the
     *  instant an enemy goes down — the punch the old blood/dust-only kill beat
     *  lacked. Pooled flat additive quads (a procedural ring texture); the ring
     *  starts at startM radius and expands to startM+growM over its life. */
    shockwaveColor: 0xffb464,
    shockwaveLifeMs: 320,
    shockwaveStartM: 0.5,
    shockwaveGrowM: 3.0,
    shockwavePoolSize: 8,

    /** KILL SPARKS (W3): a few short bright streaks flung outward from a downed
     *  enemy (reuse the tracer tube pool — no new allocation). */
    sparkCount: 4,
    sparkColor: 0xffd27a,
    sparkLenM: 0.75,
    sparkLifeMs: 200,

    /** A brief scene light pulse on a kill so downs visibly POP (reuses the
     *  fixed flash-light pool; intensity below the muzzle so it reads softer). */
    killLightIntensity: 5,
    killLightMs: 120,
  },

  /** Unified color grade (the cohesion "lens"). */
  grade: {
    bloomIntensity: 1.05,
    bloomThreshold: 0.65,
    bloomSmoothing: 0.28,
    bloomRadius: 0.72,
    vignette: 0.5,
    grain: 0.045,
    chromaticAberration: 0.0011,
    toneMappingExposure: 1.2,
    /** Final punch: a mild contrast S-curve + saturation lift glue the dusk frame
     *  into a rich cinematic read (the flat lit scene alone is too tonally
     *  compressed). Brightness nudges the lit ground up off black. Kept tasteful —
     *  this is the line between "cinematic" and "oversaturated orange soup". */
    contrast: 0.15,
    brightness: 0.05,
    saturation: 0.16,
  },
} as const);
